import React, {useCallback, useMemo, useState} from 'react'
import styled from 'styled-components'
import base64 from 'base-64'
import {AUTH_LOGIN_MAX_ATTEMPT, WINDOW_MODE_TABLET_WIDTH} from 'consts'
import {useTranslation} from 'i18n'
import {GiftShopTemplateMFAInput, TemplateAuthAccessFullPage} from 'pages'
import {requestData} from 'services'
import {showSnackbar} from 'utils'
import {useWindowMode} from 'windows'
import {Icon, Modal} from 'common/components'
import {useSelector} from 'lib/redux'
import convertUnit from 'lib/unit'

const StyledModal = styled(Modal)`
  width: 100%;
`
const StyledDesktopModal = styled(Modal)`
  width: 100%;
  max-width: ${convertUnit(375)};
  padding: ${convertUnit(25)};
  text-align: center;
`

const StyledContainer = styled.div`
  width: 100%;
  display: flex;
  text-align: center;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0;
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    padding: ${convertUnit(25)} 0;
    padding-bottom: 0;
  }
`

const StyledIconContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    padding-right: ${convertUnit(25)};
  }
`

const StyledCloseIcon = styled.div`
  cursor: pointer;
`

export interface ModalPortalGuestItemProps {
  onClose(): void
}

export default function ModalPortalGuestItem({
  onClose,
}: ModalPortalGuestItemProps) {
  const {translate} = useTranslation()
  const mode = useWindowMode()
  const {refreshToken} = useSelector('lastUserState')
  const [modal, setModal] = useState<'guest' | 'mfa'>('guest')
  const stateMFALoading = useState(false)
  const setMFALoading = stateMFALoading[1]
  const stateMFAError = useState('')
  const setMFAError = stateMFAError[1]

  const handleVerifyMFA = useCallback(
    (totp) => {
      const user = 'web'
      const password = process.env.AUTH_CLIENT_PASSWORD
      if (refreshToken) {
        requestData('auth_refresh_token', {
          data: {
            code_verifier: process.env.AUTH_CODE_VERIFIER,
            grant_type: 'relogin',
            refresh_token: refreshToken,
            mfa_code: totp,
          },
          headers: {
            Authorization: `Basic ${base64.encode(`${user}:${password}`)}`,
          },
          onRequestReceived: () => {
            setMFALoading(false)
          },
          onRequestSuccess: ({status, data: {detail}}) => {
            if (status === 200) {
              onClose && onClose()
            } else if (status === 400 && detail) {
              if (detail.mfa_attempt) {
                setMFAError(
                  translate('giftShop:settingAuthAppInvalidError', {
                    count:
                      AUTH_LOGIN_MAX_ATTEMPT - parseInt(detail.mfa_attempt, 10),
                  }),
                )
              }
            } else if (status === 423) {
              setMFAError('')
              onClose && onClose()
            } else {
              showSnackbar(
                translate('global:messageError', {context: `code-${status}`}),
              )
            }
          },
        })
      }
    },
    [onClose, refreshToken, setMFAError, setMFALoading, translate],
  )

  const handleRenderCloseIcon = useMemo(
    () => (
      <StyledIconContainer>
        <StyledCloseIcon>
          <Icon
            type="close"
            color="primary_5"
            size={convertUnit(20)}
            onClick={onClose}
          />
        </StyledCloseIcon>
      </StyledIconContainer>
    ),
    [onClose],
  )

  const handleRenderContent = useMemo(
    () =>
      modal === 'mfa' ? (
        <GiftShopTemplateMFAInput
          onCancel={onClose}
          onConfirm={(code) => handleVerifyMFA(code)}
          stateLoading={stateMFALoading}
          stateError={stateMFAError}
        />
      ) : (
        <StyledContainer>
          {handleRenderCloseIcon}
          <TemplateAuthAccessFullPage
            onClose={onClose}
            onMFA={() => setModal('mfa')}
          />
        </StyledContainer>
      ),
    [
      handleRenderCloseIcon,
      handleVerifyMFA,
      modal,
      onClose,
      stateMFAError,
      stateMFALoading,
    ],
  )

  return mode === 'mobile' ? (
    modal === 'mfa' ? (
      <StyledDesktopModal zIndex={10} visible onBackdropPress={onClose}>
        {handleRenderContent}
      </StyledDesktopModal>
    ) : (
      <StyledModal
        zIndex={10}
        visible
        isHalfScreen
        noBorderRadius
        onBackdropPress={onClose}>
        {handleRenderContent}
      </StyledModal>
    )
  ) : (
    <StyledDesktopModal zIndex={10} visible onBackdropPress={onClose}>
      {handleRenderContent}
    </StyledDesktopModal>
  )
}
